// @ts-nocheck

// services/study-year-companies.service.ts

import {
  GetStudyYearCompaniesRequest,
  GetStudyYearCompanyRequest,
  UpdateStudyYearCompanyRequest,
  UploadSurveyRequest,
  DownloadSurveyRequest,
  StudyYearCompanyDetail,
  StudyYearCompanyReadSchema,
  StudyYearCompanySchema,
  SYCategoryCompanySchema,
  PersonSchema,
} from '@/types/study-year-companies';
import { api } from './index';

class StudyYearsCompaniesService {
  // ---------------------------------- STUDY YEAR COMPANIES ----------------------------------

  /**
   * Get all study year companies with filtering (V3)
   * GET /v3/study-year-companies/
   * Supports year and study_id query parameters
   */
  async getStudyYearCompanies(
    params?: GetStudyYearCompaniesRequest
  ): Promise<StudyYearCompanyReadSchema[]> {
    try {
      const response = await api.get<StudyYearCompanyReadSchema[]>('/study-year-companies/', {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching study year companies (V3):', error);
      throw error;
    }
  }

  /**
   * Get study year company by ID (V3)
   * GET /v3/study-year-companies/{study_year_company_id}
   * Returns detailed company information including children and hierarchical data
   */
  async getStudyYearCompany(request: GetStudyYearCompanyRequest): Promise<StudyYearCompanyDetail> {
    console.log('call');
    try {
      const { studyYearCompanyId } = request;
      const response = await api.get<StudyYearCompanyDetail>(
        `/study-year-companies/${studyYearCompanyId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching study year company ${request.studyYearCompanyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Update study year company (V3)
   * PATCH /v3/study-year-companies/{study_year_company_id}
   * Supports updating status, analysis, performance data, and hierarchical data for subcategories
   */
  async updateStudyYearCompany(
    request: UpdateStudyYearCompanyRequest
  ): Promise<StudyYearCompanyDetail> {
    try {
      const { studyYearCompanyId, ...payload } = request;
      const response = await api.patch<StudyYearCompanyDetail>(
        `/study-year-companies/${studyYearCompanyId}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating study year company ${request.studyYearCompanyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Get study year company segments (V3)
   * GET /v3/study-year-companies/{study_year_company_id}/segments
   * Returns segments for a study year company
   */
  async getStudyYearCompanySegments(
    request: GetStudyYearCompanyRequest
  ): Promise<SYCategoryCompanySchema[]> {
    try {
      const { studyYearCompanyId } = request;
      const response = await api.get<SYCategoryCompanySchema[]>(
        `/study-year-companies/${studyYearCompanyId}/segments`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching segments for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }

  // ---------------------------------- SURVEY MANAGEMENT ----------------------------------

  /**
   * Download survey for study year company (V3)
   * POST /v3/study-year-companies/{study_year_company_id}/download-survey
   * Supports optional contact_id, user_id, and currency_override query parameters
   */
  async downloadSurvey(request: DownloadSurveyRequest): Promise<Blob> {
    try {
      const { studyYearCompanyId, contact_id, user_id, currency_override } = request;

      const params: Record<string, string> = {};
      if (contact_id) params.contact_id = contact_id;
      if (user_id) params.user_id = user_id;
      if (currency_override) params.currency_override = currency_override;

      const response = await api.post(
        `/study-year-companies/${studyYearCompanyId}/download-survey`,
        {},
        {
          params,
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error downloading survey for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Upload survey for study year company (V3)
   * POST /v3/study-year-companies/{study_year_company_id}/upload-survey
   * Uploads survey data using multipart/form-data
   */
  async uploadSurvey(request: UploadSurveyRequest): Promise<object> {
    try {
      const { studyYearCompanyId, file } = request;

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<object>(
        `/study-year-companies/${studyYearCompanyId}/upload-survey`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error uploading survey for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }

  // ---------------------------------- CONTACTS & VERSIONS ----------------------------------

  /**
   * Get study year company contacts (V3)
   * GET /v3/study-year-companies/{study_year_company_id}/contacts
   * Returns relevant contacts for the study year company
   */
  async getStudyYearCompanyContacts(request: GetStudyYearCompanyRequest): Promise<PersonSchema[]> {
    try {
      const { studyYearCompanyId } = request;
      const response = await api.get<PersonSchema[]>(
        `/study-year-companies/${studyYearCompanyId}/contacts`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching contacts for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get study year company versions (V3)
   * GET /v3/study-year-companies/{study_year_company_id}/versions
   * Returns versions for a study year company
   */
  async getStudyYearCompanyVersions(request: GetStudyYearCompanyRequest): Promise<object[]> {
    try {
      const { studyYearCompanyId } = request;
      const response = await api.get<object[]>(
        `/study-year-companies/${studyYearCompanyId}/versions`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching versions for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }

  /**
   * Get study year company history (V3)
   * GET /v3/study-year-companies/{study_year_company_id}/history
   * Returns historical data for the study year company
   */
  async getStudyYearCompanyHistory(request: GetStudyYearCompanyRequest): Promise<any[]> {
    try {
      const { studyYearCompanyId } = request;
      const response = await api.get<any[]>(`/study-year-companies/${studyYearCompanyId}/history`);
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching history for study year company ${request.studyYearCompanyId} (V3):`,
        error
      );
      throw error;
    }
  }
}

export const studyYearsCompaniesService = new StudyYearsCompaniesService();
